// -----------------------------------------------------------------------
// <copyright file="GetHelpCommercialFooter.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { IStackStyles, ITextStyles, Link, Stack } from '@fluentui/react';
import React from 'react';
import { appContext } from '../../models/getHelp/appContext';
import { EmeraldUserType } from '../../models/getHelp/emeraldUserType';
import { TagId } from '../../models/getHelp/GetHelpLoggingRequest';
import { getHelpLogger } from '../../utils/getHelpLogger';
import { getString } from '../../utils/stringLoader';
import { StyledText } from './StyledText';

const commercialItemsStyle: IStackStyles = {
  root: {
    alignItems: 'flex-start',
    a: {
      textDecoration: 'underline solid',
    },
  },
};

const commercialItemStyle: IStackStyles = {
  root: {
    paddingRight: '20px',
  },
};

const commercialSubtitleStyle: ITextStyles = {
  root: {
    padding: '20px 0px',
  },
};

const commercialContainerStyle: IStackStyles = {
  root: {
    paddingTop: '50px',
  },
};

const commercialItemDescriptionStyle: ITextStyles = {
  root: {
    paddingTop: '10px',
  },
};

type CommercialLinkBlockProps = {
  name: string;
  url: string;
  title: string;
  description: string;
  tagId: TagId;
};

const SmallBusinessUrl = 'https://admin.microsoft.com/AdminPortal';
const ItProUrl = 'https://support.serviceshub.microsoft.com/supportforbusiness';
const DeploymentGuideUrl = 'https://go.microsoft.com/fwlink/?linkid=2299273';

const CommercialLinkBlock = (props: CommercialLinkBlockProps): JSX.Element => (
  <Stack styles={commercialItemStyle}>
    <Link
      aria-label={props.title}
      href={props.url}
      target="_blank"
      onClick={() => getHelpLogger.logLinkClicked(props.tagId, props.name, props.url)}
      data-m={JSON.stringify({
        message: `${props.name}: Clicked link`,
        locale: appContext.locale,
        sessionId: appContext.sessionId,
        title: props.name,
        EmeraldUserType: EmeraldUserType[appContext.emeraldUserType],
      })}
    >
      {props.title}
    </Link>
    <StyledText styles={commercialItemDescriptionStyle}>{props.description}</StyledText>
  </Stack>
);

export const GetHelpCommercialFooter = (): JSX.Element => (
  <Stack id={'commercialContainer'} styles={commercialContainerStyle}>
    <Stack>
      <hr
        style={{ height: 0.1, width: '100%', alignSelf: 'center' }} // tslint:disable-line:jsx-ban-props
      />
      <StyledText styles={commercialSubtitleStyle}>
        {getString('GetHelpCommercial_ContactSupport_Description')}
      </StyledText>
    </Stack>
    <Stack horizontal styles={commercialItemsStyle}>
      <CommercialLinkBlock
        name="SmallBusinessUrl"
        url={SmallBusinessUrl}
        title={getString('GetHelpCommercial_SmallBusiness')}
        description={getString('GetHelpCommercial_SmallBusiness_Description')}
        tagId={TagId.CommercialSmallBusinessUrlClicked}
      />
      <CommercialLinkBlock
        name="ItProUrl"
        url={ItProUrl}
        title={getString('GetHelpCommercial_ITProfessionals')}
        description={getString('GetHelpCommercial_ITProfessionals_Description')}
        tagId={TagId.CommercialItProUrlClicked}
      />
      <CommercialLinkBlock
        name="DeploymentGuideUrl"
        url={DeploymentGuideUrl}
        title={getString('GetHelpCommercial_DeploymentGuide')}
        description={getString('GetHelpCommercial_DeploymentGuide_Description')}
        tagId={TagId.CommercialDeploymentGuideUrlClicked}
      />
    </Stack>
  </Stack>
);
